import { useNavigation } from '@react-navigation/native';
import { Button } from 'aimo-ui';
import useMinimumLoadingTime from 'hooks/useMinimumLoadingTime';
import useOngoingChargingSession from 'hooks/useOngoingChargingSession';
import useParkingLinkedToCharging from 'hooks/useParkingLinkedToCharging';
import { RetryPaymentPolling } from 'hooks/useRetryPaymentResult';
import useStopChargingMutation from 'hooks/useStopChargingMutation';
import useUpdateParkingMutation from 'hooks/useUpdateParkingMutation';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyledWrapper } from 'styles/ContainerStyles';
import { RootNavProps } from 'types/navigation';
import { chargingAllowedToStop } from 'utils/chargingUtils';
import ANPRChargingInfo from './terminate/ANPRChargingInfo';
import CancelTermination from './terminate/ChargingStopCancel';
import StopParkingCharging from './terminate/StopParkingCharging';

const StopCharging: React.FC = () => {
  const { t } = useTranslation();
  const { ongoingChargingSession } = useOngoingChargingSession();
  const { navigate } = useNavigation<RootNavProps>();
  const { parkingSession: hasParkingSession, allowStopOngoingParkingSession } =
    useParkingLinkedToCharging({
      parkingZoneUid: ongoingChargingSession?.parkingZoneUid,
    });

  const callback = () => {
    navigate('parkingReceipt', {
      sessionId: hasParkingSession?.sessionId as string,
      chargingSessionId: ongoingChargingSession?.chargingSessionId ?? '',
    });
  };

  const { stopChargingSessionV2, loading: stopChargingLoading } =
    useStopChargingMutation();

  const { updateParking, updateParkingLoading } = useUpdateParkingMutation({
    callback,
    sessionId: hasParkingSession?.sessionId as string,
    actionType: 'STOP',
    isModal: true,
  });

  const { showLoading } = useMinimumLoadingTime(
    updateParkingLoading || stopChargingLoading,
    5000
  );

  const handleStopParkingAndChargingSessions = () => {
    stopChargingSessionV2();
    updateParking();
  };

  const continueParking = () => {
    stopChargingSessionV2();
  };

  const stopBoth = Boolean(
    allowStopOngoingParkingSession &&
      chargingAllowedToStop(ongoingChargingSession?.status ?? 'IN_PROGRESS')
  );

  const renderContent = () => {
    if (stopBoth) {
      return (
        <StopParkingCharging
          handleStopParkingAndChargingSessions={
            handleStopParkingAndChargingSessions
          }
          continueParking={continueParking}
          loading={showLoading}
        />
      );
    }
    return (
      <StyledWrapper mb={20}>
        <Button
          size="l"
          testID="charging-stop-confirm"
          buttonType="secondary"
          text={t('evCharging.stop')}
          onPress={() => stopChargingSessionV2()}
          loading={showLoading}
          disabled={showLoading}
        />
      </StyledWrapper>
    );
  };

  return (
    <RetryPaymentPolling>
      <StyledWrapper mb={0}>
        <ANPRChargingInfo ongoingSession={hasParkingSession} />
        {renderContent()}
        <CancelTermination
          textKey={
            stopBoth ? 'evCharging.continueChargingParking' : 'button.cancel'
          }
          terminateBoth={stopBoth}
          testID="charging-stop-cancel"
        />
      </StyledWrapper>
    </RetryPaymentPolling>
  );
};
export default StopCharging;
